import { BaseApiService } from './base';
import {
  Task,
  TaskFilters,
  TaskSortField,
  TaskExecution,
  TaskMetrics,
  TaskConfiguration,
  TransferDirection,
  CreateTaskRequest,
  UpdateTaskRequest,
  EnableTaskResponse,
  HeldFilesResponse,
  DiscardHeldFilesResponse
} from '../../types/task';
import { PaginatedResponse, QueryParams } from '../../types/api';
import { snakeToCamel } from '../../utils/caseTransform';

export interface TaskExecutionRequest {
  taskId: string;
  manual?: boolean;
}

// Transform API response to camelCase TaskExecution type using generic converter
function transformExecution(apiExecution: any): TaskExecution {
  const exec = snakeToCamel<any>(apiExecution);
  return {
    executionId: exec.executionId || '',
    taskId: exec.taskId || '',
    partnerId: exec.partnerId || '',
    connectionId: exec.connectionId || '',
    executionType: exec.executionType || 'manual',
    status: exec.status || 'pending',
    startTime: exec.startTime || '',
    endTime: exec.endTime,
    duration: exec.duration,
    // THUB-204: per-file identity for hosted-inbound rows. The transform
    // whitelists fields, so these must be copied through explicitly or they'd
    // be dropped. Undefined for legacy/batch rows that don't carry them.
    filename: exec.filename,
    s3Key: exec.s3Key,
    metrics: {
      filesProcessed: exec.metrics?.filesProcessed ?? 0,
      totalFileSize: exec.metrics?.totalFileSize ?? 0,
      successCount: exec.metrics?.successCount ?? 0,
      failureCount: exec.metrics?.failureCount ?? 0,
      averageProcessingTime: exec.metrics?.averageProcessingTime ?? 0,
      // THUB-226: remote-pickup listing diagnostics. This transform WHITELISTS
      // fields, so anything not copied here is silently dropped no matter what the
      // API returns — which is why matchedFileCount (THUB-122) had existed on the
      // backend for months without ever reaching the GUI.
      //
      // THUB-278: these are deliberately NOT defaulted to 0/false. `?? 0` made
      // "the API never sent this field" and "the value really is zero"
      // indistinguishable, so a delivery/hosted execution — which performs no listing
      // at all — looked identical to a pickup that listed 0 files. Passing undefined
      // through lets the renderer tell "no listing happened" (show nothing) apart from
      // "listed some files and matched none" (show the explanation). The consumer
      // applies its own display defaults.
      matchedFileCount: exec.metrics?.matchedFileCount,
      totalFilesListed: exec.metrics?.totalFilesListed,
      skippedByPattern: exec.metrics?.skippedByPattern,
      skippedByWatermark: exec.metrics?.skippedByWatermark,
      listingTruncated: exec.metrics?.listingTruncated,
      watermarkApplied: exec.metrics?.watermarkApplied
    },
    logs: exec.logs ? {
      s3Bucket: exec.logs.s3Bucket || '',
      s3Key: exec.logs.s3Key || '',
      cloudWatchLogGroup: exec.logs.cloudWatchLogGroup || '',
      cloudWatchLogStream: exec.logs.cloudWatchLogStream || ''
    } : {
      s3Bucket: '',
      s3Key: '',
      cloudWatchLogGroup: '',
      cloudWatchLogStream: ''
    },
    error: exec.error ? {
      errorCode: exec.error.errorCode || '',
      errorMessage: exec.error.errorMessage || '',
      stackTrace: exec.error.stackTrace
    } : undefined,
    retryCount: exec.retryCount ?? 0,
    nextRetryTime: exec.nextRetryTime,
    createdAt: exec.createdAt || ''
  };
}

// T053: Helper to derive provisioning status for tasks
function deriveTaskProvisioningStatus(snsTopicArn: string | undefined): 'pending' | 'provisioning' | 'active' | 'failed' | 'deleting' {
  // Tasks with SNS topic ARN are fully provisioned
  return snsTopicArn ? 'active' : 'provisioning';
}

// T053: Helper to derive health from task execution status
function deriveTaskHealth(status: string, lastExecution: any): 'healthy' | 'warning' | 'error' | 'unknown' {
  if (status === 'disabled') {
    return 'unknown';
  }
  if (!lastExecution) {
    return 'unknown';
  }
  switch (lastExecution.status) {
    case 'completed':
      return 'healthy';
    case 'failed':
      return 'error';
    case 'running':
      return 'healthy';
    case 'pending':
      return 'warning';
    default:
      return 'unknown';
  }
}

// Transform API response to camelCase Task type using generic converter
function transformTask(apiTask: any): Task {
  const t = snakeToCamel<any>(apiTask);

  // FR-024: Get transferDirection from API response or derive from transferType
  const transferType = t.transferType || 'receive';
  const transferDirection = t.transferDirection ||
    (transferType === 'receive' || transferType === 'pickup' ? 'inbound' : 'outbound');

  const status = t.status || 'disabled';

  // T053: Derive provisioning status and health
  const provisioningStatus = t.provisioningStatus || deriveTaskProvisioningStatus(t.snsTopicArn);
  const health = t.health || deriveTaskHealth(status, t.lastExecution);

  return {
    taskId: t.taskId || '',
    name: t.name || '',
    // THUB-160: free-form display label (optional). Left undefined when absent so
    // callers can fall back to `name` rather than rendering an empty string.
    description: t.description || undefined,
    partnerId: t.partnerId || '',
    partnerName: t.partnerName || '',
    connectionId: t.connectionId || '',
    connectionName: t.connectionName || '',
    prefix: t.prefix || '',
    transferDirection,
    status,
    provisioningStatus,
    health,
    schedule: t.schedule ? {
      type: t.schedule.type || 'manual',
      expression: t.schedule.expression || '',
      enabled: t.schedule.enabled ?? false,
      timezone: t.schedule.timezone || 'UTC',
      nextRun: t.schedule.nextRun || ''
    } : undefined,
    configuration: t.configuration || {},
    metrics: {
      totalExecutions: t.metrics?.totalExecutions ?? 0,
      successfulExecutions: t.metrics?.successfulExecutions ?? 0,
      failedExecutions: t.metrics?.failedExecutions ?? 0,
      totalFilesProcessed: t.metrics?.totalFilesProcessed ?? 0,
      totalBytesTransferred: t.metrics?.totalBytesTransferred ?? 0,
      averageExecutionTime: t.metrics?.averageExecutionTime ?? 0
    },
    lastExecution: t.lastExecution ? {
      executionId: t.lastExecution.executionId || '',
      status: t.lastExecution.status || 'pending',
      startTime: t.lastExecution.startTime || '',
      endTime: t.lastExecution.endTime || '',
      filesProcessed: t.lastExecution.filesProcessed ?? 0,
      bytesTransferred: t.lastExecution.bytesTransferred ?? 0,
      errorMessage: t.lastExecution.errorMessage || ''
    } : undefined,
    // FR-042/043 + THUB-142: outbound delivery mode/schedule for remote+outbound tasks
    deliveryMode: t.deliveryMode,
    // THUB-223: post-pickup action (remote+inbound). These were MISSING from this literal
    // while every sibling workflow field (deliveryMode, deliverySchedule, all the HTTP
    // fields) was present, so they were silently dropped on every read.
    //
    // That is the "not saving / getting reset" bug: the PUT succeeded and the value WAS
    // persisted, but reloading the task returned an object without these keys, so
    // TaskEditor's `useState(task.postPickupAction || 'none')` fell back to 'none' and the
    // form redisplayed the default. The user sees their change revert and reasonably
    // concludes the save failed. Nothing was ever lost -- only never read back.
    //
    // Verified against DEV: the API DOES return post_pickup_action, and snakeToCamel above
    // DOES convert it to postPickupAction -- it was this explicit allowlist that discarded
    // it. A transform that enumerates fields silently drops anything a later feature adds.
    postPickupAction: t.postPickupAction,
    postPickupTargetPath: t.postPickupTargetPath,
    deliverySchedule: t.deliverySchedule,
    deliveryScheduleArn: t.deliveryScheduleArn,
    // HTTP task fields (019)
    httpDeliveryMode: t.httpDeliveryMode,
    httpResponseMode: t.httpResponseMode,
    httpResourcePath: t.httpResourcePath,
    httpMethodOverride: t.httpMethodOverride,
    httpContentTypeOverride: t.httpContentTypeOverride,
    requestBodyTemplate: t.requestBodyTemplate,
    metadataFields: t.metadataFields,
    enableBase64: t.enableBase64,
    // PGP encryption (021)
    pgpEnabled: t.pgpEnabled || false,
    // Developer integration data (populated by backend from partner/task resources)
    integration: t.integration ? {
      bucketName: t.integration.bucketName,
      bucketArn: t.integration.bucketArn,
      kmsKeyArn: t.integration.kmsKeyArn,
      kmsKeyId: t.integration.kmsKeyId,
      snsTopicArn: t.integration.snsTopicArn,
      region: t.integration.region,
      accountId: t.integration.accountId,
      dropPath: t.integration.dropPath,
      archivePath: t.integration.archivePath,
      iamPolicy: t.integration.iamPolicy,
    } : undefined,
    createdAt: t.createdAt || '',
    updatedAt: t.updatedAt || '',
    createdBy: t.createdBy || 'system',
    version: t.version
  };
}

export class TasksApiService extends BaseApiService {
  // Task CRUD operations
  async getTasks(filters?: TaskFilters, sortBy?: TaskSortField, sortOrder?: 'asc' | 'desc'): Promise<Task[]> {
    const params: QueryParams = {};
    
    if (filters) {
      params.filters = [];
      
      if (filters.status.length > 0) {
        params.filters.push({
          field: 'status',
          operator: 'in',
          value: filters.status
        });
      }
      
      if (filters.partners.length > 0) {
        params.filters.push({
          field: 'partnerId',
          operator: 'in',
          value: filters.partners
        });
      }
      
      if (filters.connections.length > 0) {
        params.filters.push({
          field: 'connectionId',
          operator: 'in',
          value: filters.connections
        });
      }
      
      if (filters.transferDirections.length > 0) {
        params.filters.push({
          field: 'transferDirection',
          operator: 'in',
          value: filters.transferDirections
        });
      }
      
      if (filters.scheduleTypes.length > 0) {
        params.filters.push({
          field: 'schedule.type',
          operator: 'in',
          value: filters.scheduleTypes
        });
      }
      
      if (filters.dateRange) {
        params.dateRange = filters.dateRange;
      }
    }
    
    if (sortBy && sortOrder) {
      params.sort = [{
        field: sortBy,
        order: sortOrder
      }];
    }

    const response = await this.get('/tasks', params);
    const data = await response.json(); return (data.tasks || []).map(transformTask);
  }

  async getTasksPaginated(
    page: number = 1,
    limit: number = 20,
    filters?: TaskFilters,
    sortBy?: TaskSortField,
    sortOrder?: 'asc' | 'desc'
  ): Promise<PaginatedResponse<Task>> {
    const params: QueryParams = {
      pagination: { page, limit, sortBy, sortOrder }
    };
    
    if (filters) {
      params.filters = [];
      
      if (filters.status.length > 0) {
        params.filters.push({
          field: 'status',
          operator: 'in',
          value: filters.status
        });
      }
      
      if (filters.partners.length > 0) {
        params.filters.push({
          field: 'partnerId',
          operator: 'in',
          value: filters.partners
        });
      }
      
      if (filters.connections.length > 0) {
        params.filters.push({
          field: 'connectionId',
          operator: 'in',
          value: filters.connections
        });
      }
      
      if (filters.transferDirections.length > 0) {
        params.filters.push({
          field: 'transferDirection',
          operator: 'in',
          value: filters.transferDirections
        });
      }
      
      if (filters.scheduleTypes.length > 0) {
        params.filters.push({
          field: 'schedule.type',
          operator: 'in',
          value: filters.scheduleTypes
        });
      }
      
      if (filters.dateRange) {
        params.dateRange = filters.dateRange;
      }
    }

    return this.getPaginated<PaginatedResponse<Task>>('/tasks', params);
  }

  async getTask(id: string): Promise<Task> {
    const response = await this.get(`/tasks/${id}`);
    const data = await response.json();
    return transformTask(data);
  }

  // THUB-223: `this.post`/`this.put` resolve to a fetch `Response`, NOT parsed JSON — see
  // BaseApiService. Both of these passed that Response straight to transformTask, which then
  // read `.task_id`, `.name` etc. off the Response object and got `undefined` for EVERY
  // field. So the returned Task was entirely blank on create and update.
  //
  // It went unnoticed because callers refetch the list afterwards and render from that, so
  // the screen looks right; only code that consumes the RETURN VALUE sees the empty object.
  // `getTask` and `patchTask` always awaited `.json()` — these two were the outliers.
  async createTask(taskData: CreateTaskRequest): Promise<Task> {
    const response = await this.post('/tasks', taskData);
    const data = await response.json();
    return transformTask(data);
  }

  async updateTask(id: string, taskData: UpdateTaskRequest): Promise<Task> {
    const response = await this.put(`/tasks/${id}`, taskData);
    const data = await response.json();
    return transformTask(data);
  }

  async patchTask(id: string, data: Partial<UpdateTaskRequest>): Promise<Task> {
    const response = await this.patch(`/tasks/${id}`, data);
    const json = await response.json();
    return transformTask(json);
  }

  async deleteTask(id: string, options?: { version?: number }): Promise<void> {
    const params = new URLSearchParams();
    if (options?.version !== undefined) params.set('version', String(options.version));
    const qs = params.toString();
    await this.delete(`/tasks/${id}${qs ? `?${qs}` : ''}`);
  }

  // Task status management
  // THUB-149: enable is a two-step probe/confirm. Without deliverHeld, the backend
  // returns {requiresConfirmation, heldCount, ...} and does NOT enable when held files
  // exist. With deliverHeld:true it enables and flushes the held files.
  //
  // THUB-152: `heldFileIds` optionally narrows the flush to a selection. Passing
  // `undefined` OMITS the key entirely, which the backend reads as "flush all eligible"
  // — the pre-THUB-152 behavior. An explicit `[]` means "enable but deliver nothing", so
  // the two must not be conflated here either.
  async enableTask(
    id: string,
    deliverHeld = false,
    heldFileIds?: string[]
  ): Promise<EnableTaskResponse> {
    const body = deliverHeld
      ? { deliverHeld: true, ...(heldFileIds !== undefined ? { heldFileIds } : {}) }
      : undefined;
    const response = await this.put(`/tasks/${id}/enable`, body);
    return (await response.json()) as EnableTaskResponse;
  }

  // THUB-152: held-file review. Returns per-file detail (size, age, reason, archive
  // location, transferMethod, withMetadata, cold-storage/missing) for every file still
  // awaiting delivery on this task.
  async getHeldFiles(taskId: string): Promise<HeldFilesResponse> {
    const response = await this.get(`/tasks/${taskId}/held-files`);
    return (await response.json()) as HeldFilesResponse;
  }

  // THUB-152: discard ONE held file — it will not be delivered on enable. Pointer-only:
  // the archived bytes are retained (retention/lifecycle governs them).
  async discardHeldFile(taskId: string, heldFileId: string): Promise<void> {
    await this.delete(`/tasks/${taskId}/held-files/${encodeURIComponent(heldFileId)}`);
  }

  // THUB-152: discard several held files, or every one of them with {all:true}. The
  // backend rejects an absent/empty selection with a 400 rather than treating it as
  // "discard everything", so callers must be explicit.
  async discardHeldFiles(
    taskId: string,
    selection: { heldFileIds: string[] } | { all: true }
  ): Promise<DiscardHeldFilesResponse> {
    const response = await this.delete(`/tasks/${taskId}/held-files`, selection);
    return (await response.json()) as DiscardHeldFilesResponse;
  }

  async disableTask(id: string): Promise<Task> {
    const response = await this.put(`/tasks/${id}/disable`);
    const data = await response.json();
    return transformTask(data);
  }

  // Task execution
  async executeTask(request: TaskExecutionRequest): Promise<TaskExecution> {
    const response = await this.post('/tasks/execute', request);
    return response.json();
  }

  async executeTaskManually(taskId: string): Promise<TaskExecution> {
    const response = await this.post(`/tasks/${taskId}/execute`, { manual: true });
    const data = await response.json();
    return transformExecution(data);
  }

  async getTaskExecution(executionId: string): Promise<TaskExecution> {
    const response = await this.get(`/tasks/executions/${executionId}`);
    const data = await response.json();
    return transformExecution(data);
  }

  async getTaskExecutions(
    taskId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<TaskExecution>> {
    const params: QueryParams = {
      pagination: { page, limit },
      filters: [{
        field: 'taskId',
        operator: 'eq',
        value: taskId
      }]
    };

    return this.getPaginated<PaginatedResponse<TaskExecution>>('/tasks/executions', params);
  }

  // THUB-131: task-scoped executions endpoint for monitor hydration. Uses the
  // /tasks/{id}/executions route which permits Builders (the global /tasks/executions
  // endpoint is system_admin only).
  async getExecutionsForTask(taskId: string, limit: number = 1): Promise<TaskExecution[]> {
    const response = await this.get(`/tasks/${taskId}/executions?limit=${limit}`);
    const data = await response.json();
    const raw = Array.isArray(data?.executions) ? data.executions : [];
    return raw.map((e: unknown) => transformExecution(e));
  }

  // THUB-187: cursor-paginated task-scoped executions for the Task History tab.
  // Contract (lane2 #227): GET /tasks/{id}/executions?limit=<1..50>&cursor=<opaque>
  //  - OMIT cursor for the first page.
  //  - Response adds `nextCursor: string | null` (null = end of history).
  //  - cursor is OPAQUE base64 — pass it back verbatim, never parse/construct it.
  //  - Server clamps limit>50 to 50; bad cursor -> 400 INVALID_CURSOR; bad limit
  //    -> 400 INVALID_LIMIT (surfaced to the caller as a thrown ApiError).
  async getExecutionsPageForTask(
    taskId: string,
    options: { limit?: number; cursor?: string } = {}
  ): Promise<{ executions: TaskExecution[]; nextCursor: string | null }> {
    const params = new URLSearchParams();
    // Clamp client-side to the contract's 1..50 so we never send an obviously
    // bad limit; the server also clamps, this just avoids a needless 400.
    const limit = Math.min(50, Math.max(1, options.limit ?? 50));
    params.set('limit', String(limit));
    if (options.cursor) params.set('cursor', options.cursor);

    const response = await this.get(`/tasks/${taskId}/executions?${params.toString()}`);
    const data = await response.json();
    const raw = Array.isArray(data?.executions) ? data.executions : [];
    return {
      executions: raw.map((e: unknown) => transformExecution(e)),
      // Treat any falsy/absent value as end-of-history (null).
      nextCursor: data?.nextCursor ?? null,
    };
  }

  async cancelTaskExecution(executionId: string): Promise<TaskExecution> {
    const response = await this.put(`/tasks/executions/${executionId}/cancel`);
    const data = await response.json();
    return transformExecution(data);
  }

  // Task metrics
  async getTaskMetrics(taskId: string): Promise<TaskMetrics> {
    const response = await this.get(`/tasks/${taskId}/metrics`);
    return response.json();
  }

  // THUB-198: removed getTaskPerformanceMetrics + getTaskDiagnostics — they called
  // /tasks/{id}/performance-metrics and /tasks/{id}/diagnostics, which are nonexistent
  // routes (net::ERR_FAILED). Overview already shows real Performance Metrics
  // (post-THUB-161); diagnostics described a Node-server shape meaningless for Lambda.

  // THUB-183: SNS topic subscribers (consumer blast-radius visibility, identity-only)
  async getTaskSubscribers(taskId: string): Promise<{
    taskId: string;
    snsTopicArn: string | null;
    subscribers: Array<{
      protocol: string;
      endpoint: string;
      ownerAccountId: string;
      subscriptionArn: string;
      status: 'confirmed' | 'pending';
    }>;
    count: number;
  }> {
    const response = await this.get(`/tasks/${taskId}/subscribers`);
    return response.json();
  }

  async getTasksByPartner(partnerId: string): Promise<Task[]> {
    const params: QueryParams = {
      filters: [{
        field: 'partnerId',
        operator: 'eq',
        value: partnerId
      }]
    };

    const response = await this.get('/tasks', params);
    const data = await response.json(); return (data.tasks || []).map(transformTask);
  }

  async getTasksByConnection(connectionId: string): Promise<Task[]> {
    const params: QueryParams = {
      filters: [{
        field: 'connectionId',
        operator: 'eq',
        value: connectionId
      }]
    };

    const response = await this.get('/tasks', params);
    const data = await response.json(); return (data.tasks || []).map(transformTask);
  }

  // Task validation
  async validateTaskConfiguration(
    transferDirection: TransferDirection,
    configuration: TaskConfiguration
  ): Promise<{ valid: boolean; errors: string[] }> {
    const response = await this.post(
      '/tasks/validate-configuration',
      { transferDirection, configuration }
    );
    return response.json();
  }

  async validateScheduleExpression(
    scheduleType: 'cron' | 'rate',
    expression: string
  ): Promise<{ valid: boolean; nextRuns: Date[]; error?: string }> {
    const response = await this.post(
      '/tasks/validate-schedule',
      { scheduleType, expression }
    );
    return response.json();
  }

  // T045: Create transfer for with_metadata HTTP tasks
  async createTransfer(
    taskId: string,
    s3Key: string,
    metadata: Record<string, string>
  ): Promise<{
    transferId: string;
    taskId: string;
    s3Key: string;
    status: string;
    executionArn: string;
  }> {
    const response = await this.post(`/tasks/${taskId}/transfers`, {
      s3Key,
      metadata,
    });
    const data = await response.json();
    return data;
  }
}

// Export singleton instance
export const tasksApi = new TasksApiService();